import { BTGPTTranscriptMessage } from './btgpt-server-client';

/**
 * A btgpt turn that outlived the live stream's budget ends with an `error` chunk
 * carrying `code: 'pending'`: the agent is still working and its answer will be
 * saved to the conversation. The view then re-reads the saved transcript until
 * that answer is there — this is the loop, kept pure so it is testable.
 */

/** How often to re-read the transcript, and for how long at most. */
export const PENDING_POLL_MS = 3_000;
export const PENDING_MAX_MS = 10 * 60_000;

/** True when the saved transcript already holds an assistant answer after its last user message. */
export function hasReplyAfterLastUser(saved: BTGPTTranscriptMessage[]): boolean {
  let lastUser = -1;
  saved.forEach((m, i) => {
    if (m.role === 'user') lastUser = i;
  });
  return lastUser >= 0 && saved.slice(lastUser + 1).some((m) => m.role === 'assistant' && (m.content ?? '').trim() !== '');
}

/**
 * Re-read `load()` every `intervalMs` until the answer lands (resolves the saved
 * transcript), the time budget runs out, or `signal` aborts (both resolve null).
 */
export async function waitForSavedReply(
  load: () => Promise<BTGPTTranscriptMessage[]>,
  signal: AbortSignal,
  { intervalMs = PENDING_POLL_MS, maxMs = PENDING_MAX_MS } = {}
): Promise<BTGPTTranscriptMessage[] | null> {
  const deadline = Date.now() + maxMs;
  while (!signal.aborted && Date.now() < deadline) {
    await new Promise<void>((resolve) => {
      const onAbort = () => {
        clearTimeout(id);
        resolve();
      };
      const id = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, intervalMs);
      signal.addEventListener('abort', onAbort, { once: true });
    });
    if (signal.aborted) return null;
    try {
      const saved = await load();
      if (signal.aborted) return null;
      if (hasReplyAfterLastUser(saved)) return saved;
    } catch {
      /* a failed read is retried on the next tick */
    }
  }
  return null;
}
